/** Карточка товара у узлов «Артикул» и «SKU»/«nmID» дерева: фото площадки, артикул, SKU (nmID),
 *  названия 1С и площадки; рядом — иконка-ссылка на страницу товара площадки (по `mp_sku`).
 *
 *  `@radix-ui/react-hover-card` в зависимостях нет — карточка на Popover: открывается наведением
 *  или фокусом с задержкой (не мигает при проводке мышью по дереву), закрывается, когда мышь ушла
 *  и с узла, и с карточки, по blur, Esc или клику мимо. Клик по узлу карточку не открывает —
 *  он раскрывает узел, как и раньше. Фокус карточка не забирает. */
import { ExternalLink, PanelRight } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";

import type { TreeProduct } from "@/api/ozonAnalytics";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from "@/components/ui/popover";
import { useMp } from "./mp";
import { isRestoringDialogFocus } from "./useDialogFocus";

const OPEN_DELAY_MS = 300;
const CLOSE_DELAY_MS = 150;

/** Название товара для подписи и alt: 1С, иначе площадки. */
function productTitle(p: TreeProduct, skuLabel: string): string {
  return p.name_1c ?? p.name_mp ?? (p.mp_sku !== null ? `${skuLabel} ${p.mp_sku}` : "товар");
}

/** WB: товар сопоставлен с 1С неоднозначно — предупреждение (unmapped и так видно: «1С: не
 *  сопоставлено»). */
const MAPPING_TEXT: Partial<Record<NonNullable<TreeProduct["mapping_status"]>, string>> = {
  ambiguous: "сопоставление с 1С неоднозначно",
};

/** Иконка-ссылка на страницу товара площадки по `mp_sku`; нет его — ничего. Клик не раскрывает
 *  узел. */
export function ProductLink({
  mpSku,
}: {
  mpSku: string | null;
}): React.ReactElement | null {
  const mp = useMp();
  const href = mp.productUrl(mpSku);
  if (!href) return null;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`Открыть на ${mp.label}`}
      title={`Открыть на ${mp.label}`}
      onClick={(e) => e.stopPropagation()}
      className="ml-1 inline-flex align-[-2px] text-muted-foreground hover:text-foreground"
    >
      <ExternalLink className="size-3.5" aria-hidden />
    </a>
  );
}

/** Фото товара: 160×160 во всплывающей карточке, `small` — 64×64 в шапке карточки артикула. */
export function ProductPhoto({
  url,
  alt,
  small = false,
}: {
  url: string | null;
  alt: string;
  small?: boolean;
}): React.ReactElement {
  const mp = useMp();
  const [failed, setFailed] = useState(false);
  const size = small ? "size-16" : "size-40";
  return (
    <div
      className={`${small ? "shrink-0" : "mx-auto"} flex ${size} items-center justify-center overflow-hidden rounded border bg-muted/30`}
    >
      {url && !failed ? (
        <img
          src={url}
          alt={alt}
          loading="lazy"
          className={`${size} object-contain`}
          onError={() => setFailed(true)}
        />
      ) : (
        <span
          className={`px-1 text-center text-muted-foreground ${small ? "text-[10px] leading-tight" : "text-xs"}`}
        >
          {url ? "фото не загрузилось" : `нет фото у ${mp.label}`}
        </span>
      )}
    </div>
  );
}

/** Содержимое карточки. */
export function ProductCardBody({
  product,
  onOpenCard,
}: {
  product: TreeProduct;
  /** открыть карточку артикула (боковая панель); нет — кнопки нет */
  onOpenCard?: () => void;
}): React.ReactElement {
  const mp = useMp();
  const p = product;
  const more = p.sku_count > 1 ? ` · ещё ${p.sku_count - 1} ${mp.skuLabel}` : "";
  const mapping = p.mapping_status ? MAPPING_TEXT[p.mapping_status] : undefined;
  return (
    <div className="w-64 space-y-2 text-xs" data-testid="product-card">
      {/* key: другое фото — заново пробуем загрузить */}
      <ProductPhoto
        key={p.image_url ?? ""}
        url={p.image_url}
        alt={productTitle(p, mp.skuLabel)}
      />
      <div className="space-y-1 leading-snug">
        <div>
          <span className="text-muted-foreground">Артикул: </span>
          {p.offer_id ?? "—"}
        </div>
        <div>
          <span className="text-muted-foreground">{mp.skuLabel}: </span>
          {p.mp_sku ?? "—"}
          {more && <span className="text-muted-foreground">{more}</span>}
        </div>
        <div>
          <span className="text-muted-foreground">1С: </span>
          {p.name_1c !== null ? (
            <>
              {p.name_1c}
              {p.code_1c && (
                <span className="text-muted-foreground">
                  {" "}
                  · код {p.code_1c}
                </span>
              )}
            </>
          ) : (
            <span className="text-muted-foreground">не сопоставлено</span>
          )}
        </div>
        <div>
          <span className="text-muted-foreground">{mp.label}: </span>
          {p.name_mp ?? "—"}
        </div>
        {mapping && (
          <div className="text-amber-700 dark:text-amber-400" data-testid="mapping-status">
            {mapping}
          </div>
        )}
      </div>
      {onOpenCard && (
        <button
          type="button"
          className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
          onClick={onOpenCard}
        >
          <PanelRight className="size-3.5" aria-hidden />
          Открыть карточку
        </button>
      )}
    </div>
  );
}

/** Обёртка подписи узла: наведение/фокус → карточка товара. */
export function ProductHover({
  product,
  onOpenCard,
  children,
}: {
  product: TreeProduct;
  /** открыть карточку артикула: кнопка «Открыть карточку» во всплывашке */
  onOpenCard?: () => void;
  children: React.ReactNode;
}): React.ReactElement {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const openTimer = useRef<number | null>(null);
  const closeTimer = useRef<number | null>(null);

  const clearTimers = (): void => {
    if (openTimer.current !== null) window.clearTimeout(openTimer.current);
    if (closeTimer.current !== null) window.clearTimeout(closeTimer.current);
    openTimer.current = null;
    closeTimer.current = null;
  };
  useEffect(() => clearTimers, []);

  const scheduleOpen = (): void => {
    if (closeTimer.current !== null) window.clearTimeout(closeTimer.current);
    closeTimer.current = null;
    if (open || openTimer.current !== null) return;
    openTimer.current = window.setTimeout(() => {
      openTimer.current = null;
      setOpen(true);
    }, OPEN_DELAY_MS);
  };
  const scheduleClose = (): void => {
    if (openTimer.current !== null) window.clearTimeout(openTimer.current);
    openTimer.current = null;
    closeTimer.current = window.setTimeout(() => {
      closeTimer.current = null;
      setOpen(false);
    }, CLOSE_DELAY_MS);
  };
  // тач эмулирует pointerenter перед кликом — тап по узлу раскрывает его, карточку не открываем
  const onEnter = (e: React.PointerEvent): void => {
    if (e.pointerType !== "touch") scheduleOpen();
  };
  const onLeave = (e: React.PointerEvent): void => {
    if (e.pointerType !== "touch") scheduleClose();
  };

  return (
    <Popover
      open={open}
      onOpenChange={(v) => {
        if (!v) {
          clearTimers();
          setOpen(false);
        }
      }}
    >
      <PopoverAnchor asChild>
        <div
          ref={anchorRef}
          tabIndex={0}
          className="w-fit max-w-full rounded outline-none focus-visible:ring-1 focus-visible:ring-ring"
          onPointerEnter={onEnter}
          onPointerLeave={onLeave}
          onFocus={(e) => {
            // только фокус с клавиатуры; возврат фокуса после закрытия панели — не в счёт
            if (isRestoringDialogFocus() || !e.currentTarget.matches(":focus-visible")) return;
            scheduleOpen();
          }}
          onBlur={scheduleClose}
          data-testid="product-hover"
        >
          {children}
        </div>
      </PopoverAnchor>
      <PopoverContent
        className="w-auto p-3"
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => e.preventDefault()}
        onPointerEnter={onEnter}
        onPointerLeave={onLeave}
      >
        <ProductCardBody
          product={product}
          onOpenCard={
            onOpenCard &&
            (() => {
              // фокус — на подпись узла: панель вернёт его сюда при закрытии (кнопка
              // всплывашки к тому времени размонтирована)
              anchorRef.current?.focus();
              clearTimers();
              setOpen(false);
              onOpenCard();
            })
          }
        />
      </PopoverContent>
    </Popover>
  );
}
